import { currencyFormat } from "@Helpers/support";

/**
 * The discount a billable currently receives, from
 * `App\Http\Resources\BillingCouponResource`.
 */
export interface DiscountData {
  id?: string | null
  code?: string | null
  name?: string | null
  currency?: string | null
  duration_type?: string | null
  duration_in_months?: number | null
  amount_off?: number | null
  percent_off?: number | null
}

/**
 * Discount smart object with the wording and the formatting the banner needs.
 */
export interface Discount extends DiscountData {
  formattedAmount: string
  durationType: string
  durationInMonths: number
  wordingKey: () => string
}

/**
 * How long a discount lasts, from the `duration_type` the coupon resource sends.
 */
export const DISCOUNT_DURATION = {
  FOREVER: "forever",
  ONCE: "once",
  REPEATING: "repeating",
};

/**
 * The sentence the banner reads, one per way a discount can run out.
 */
const WORDING_KEYS = {
  FOREVER: "settings_billing_page.discount.forever",
  ONCE: "settings_billing_page.discount.once",
  ONE_MONTH: "settings_billing_page.discount.one_month",
  MANY_MONTHS: "settings_billing_page.discount.many_months",
};

/**
 * Creates a Discount smart object from raw server data.
 */
export default function DiscountModel(data: DiscountData): Discount {
  const durationType: string = data.duration_type ?? "";
  const durationInMonths: number = Math.round(data.duration_in_months ?? 0);
  const percentOff: number = data.percent_off ?? 0;

  const that: Discount = {
    ...data,
    durationType,
    durationInMonths,

    /**
     * What the discount takes off, as a percentage when it is one and as money
     * otherwise. The amount arrives in the currency's minor units.
     */
    formattedAmount: percentOff > 0
      ? `${percentOff}%`
      : currencyFormat(data.amount_off ?? 0, data.currency ?? "USD"),

    /**
     * The translation key for the sentence that describes how long the discount runs,
     * or an empty string when the coupon says nothing about its duration.
     */
    wordingKey(): string {
      if (durationType === DISCOUNT_DURATION.FOREVER) {
        return WORDING_KEYS.FOREVER;
      }

      if (durationType === DISCOUNT_DURATION.ONCE) {
        return WORDING_KEYS.ONCE;
      }

      if (durationType !== DISCOUNT_DURATION.REPEATING) {
        return "";
      }

      return durationInMonths > 1 ? WORDING_KEYS.MANY_MONTHS : WORDING_KEYS.ONE_MONTH;
    },
  };

  return that;
}
